import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, IconButton, Input, InputAdornment, Card, CardContent, Skeleton, useTheme, useMediaQuery } from '@mui/material';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useNavigate } from 'react-router-dom';
import { fetchChatThreads, searchChatThreads } from 'services/agentService';
import { useSelector } from 'react-redux';

// Narrow left-side history menu for Agent routes, rendered from MainLayout.
// - Positioned immediately to the right of the primary drawer (like other secondary menus)
// - Uses same data sources as ChatSidebar HistoryDrawer (threads + search)
// - No date line (title only)
// - Includes loading states, search, and minimize/expand behavior controlled by MainLayout

const THREADS_PAGE_SIZE = 16;

const AgentHistoryMenu = ({ minimized, onMinimizeToggle }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery((t) => t.breakpoints.down('sm'));
  const navigate = useNavigate();
  const drawerOpen = useSelector((state) => state.menu.drawerOpen);

  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);

  const [isSearchMode, setIsSearchMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchPage, setSearchPage] = useState(1);
  const [searchNumPages, setSearchNumPages] = useState(1);

  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Ref-based locks to prevent multiple simultaneous loads
  const isLoadingHistoryRef = useRef(false);
  const isLoadingSearchRef = useRef(false);

  const effectiveLoading = isSearchMode ? searchLoading : loading;
  const effectiveItems = isSearchMode ? searchResults : threads;

  const loadThreads = async (pageToLoad = 1, append = false) => {
    try {
      setLoading(true);
      const {
        items,
        page: p,
        num_pages
      } = await fetchChatThreads({
        page: pageToLoad,
        page_size: THREADS_PAGE_SIZE
      });
      const nextItems = Array.isArray(items) ? items : [];
      setThreads((prev) => (append ? [...prev, ...nextItems] : nextItems));
      if (!append) {
        setPage(Number(p) || pageToLoad || 1);
      } else {
        setPage(pageToLoad);
      }
      setNumPages(Number(num_pages) || 1);
    } catch (e) {
      setThreads([]);
      setPage(1);
      setNumPages(1);
    } finally {
      setLoading(false);
      isLoadingHistoryRef.current = false; // Release lock

      // After appending, scroll up slightly to exit the trigger zone
      // so user must intentionally scroll down again
      if (append && listRef.current) {
        requestAnimationFrame(() => {
          if (listRef.current) {
            listRef.current.scrollTop = listRef.current.scrollTop - 2;
          }
        });
      }
    }
  };

  // Initial load
  useEffect(() => {
    loadThreads(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Focus input when entering search mode & reset when exiting
  useEffect(() => {
    if (isSearchMode) {
      setTimeout(() => {
        try {
          inputRef.current && inputRef.current.focus();
        } catch (_) {
          /* ignore */
        }
      }, 0);
    } else {
      setSearchTerm('');
      setSearchResults([]);
      setSearchPage(1);
      setSearchNumPages(1);
    }
  }, [isSearchMode]);

  // Debounced search (infinite-style, paged results)
  useEffect(() => {
    if (!isSearchMode) return;
    const term = (searchTerm || '').trim();
    if (term.length < 3) {
      setSearchResults([]);
      setSearchNumPages(1);
      return;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        setSearchLoading(true);
        const {
          items,
          page: p,
          num_pages
        } = await searchChatThreads({
          q: term,
          page: searchPage,
          page_size: THREADS_PAGE_SIZE
        });
        if (cancelled) return;
        const nextItems = Array.isArray(items) ? items : [];
        setSearchResults((prev) => (searchPage > 1 ? [...prev, ...nextItems] : nextItems));
        if (searchPage === 1) {
          setSearchPage(Number(p) || 1);
        }
        setSearchNumPages(Number(num_pages) || 1);
      } catch (e) {
        if (!cancelled) {
          setSearchResults([]);
          setSearchNumPages(1);
        }
      } finally {
        if (!cancelled) {
          setSearchLoading(false);
          isLoadingSearchRef.current = false; // Release search lock

          // After appending search results, scroll up slightly to exit trigger zone
          if (searchPage > 1 && listRef.current) {
            requestAnimationFrame(() => {
              if (listRef.current) {
                listRef.current.scrollTop = listRef.current.scrollTop - 2;
              }
            });
          }
        }
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [isSearchMode, searchTerm, searchPage]);

  const handleToggleSearch = () => {
    setIsSearchMode((prev) => !prev);
  };

  // Infinite scroll handler - with strict lock to prevent cascade loading
  const handleScroll = (event) => {
    const target = event.target;
    if (!target) return;
    const { scrollTop, scrollHeight, clientHeight } = target;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 120;
    if (!nearBottom) return;

    // Non-search mode
    if (!isSearchMode) {
      // Check ref-based lock FIRST to prevent multiple simultaneous loads
      if (isLoadingHistoryRef.current) return;
      if (loading) return;
      if (page >= numPages) return;

      // Set lock IMMEDIATELY before triggering load
      isLoadingHistoryRef.current = true;
      const nextPage = page + 1;
      setPage(nextPage);
      loadThreads(nextPage, true);
      return;
    }

    // Search mode
    if (isLoadingSearchRef.current) return;
    if (searchLoading) return;
    if (searchPage >= searchNumPages) return;

    // Set lock IMMEDIATELY before triggering load
    isLoadingSearchRef.current = true;
    const nextSearchPage = searchPage + 1;
    setSearchPage(nextSearchPage);
  };

  const handleOpenThread = (id) => {
    if (!id) return;
    try {
      navigate(`/agent/${encodeURIComponent(id)}`);
    } catch (_) {
      /* ignore */
    }
  };

  const handleMinimizeToggle = () => {
    if (onMinimizeToggle) {
      onMinimizeToggle(!minimized);
    }
  };

  // Prevent scroll chaining to the main page and keep wheel events inside the history list
  const handleWheelCapture = (event) => {
    try {
      event.stopPropagation();
    } catch (_) {
      /* ignore */
    }
  };

  // Compute left offset so we sit directly next to the main drawer
  // Mirror LP analysis secondary menu so there is no visual gap.
  const getMainMenuWidth = () => (drawerOpen ? 195 : 64);

  const renderSkeletons = (count = 3) =>
    Array.from({ length: count }).map((_, idx) => (
      <Card
        key={`sk-${idx}`}
        elevation={0}
        sx={{
          mb: 0.25,
          borderRadius: 0.75,
          border: 'none',
          boxShadow: 'none',
          bgcolor: (th) => th.palette.background.paper
        }}
      >
        <CardContent sx={{ py: 0.5, px: 0.75 }}>
          <Skeleton variant="text" width="80%" height={18} />
        </CardContent>
      </Card>
    ));

  // Don't render on mobile - users access history from top menu instead
  if (isMobile) {
    return null;
  }

  if (minimized) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: isMobile ? 80 : 12,
          left: isMobile ? 0 : getMainMenuWidth(),
          zIndex: 2500
        }}
      >
        <IconButton
          size="small"
          onClick={handleMinimizeToggle}
          sx={{
            borderRadius: '50%',
            boxShadow: 1,
            bgcolor: 'background.paper'
          }}
          aria-label="Expand history menu"
        >
          <ChevronRightIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: isMobile ? 0 : getMainMenuWidth(),
        right: isMobile ? 0 : 'auto',
        width: isMobile ? 260 : 200,
        height: '100vh',
        borderRight: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        zIndex: isMobile ? 1301 : 999
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 1.5,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
          bgcolor: 'background.paper'
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          History
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {isSearchMode && (
            <Box
              component="button"
              onClick={() => setIsSearchMode(false)}
              style={{
                border: '1px solid',
                borderColor: theme.palette.divider,
                background: 'transparent',
                borderRadius: 6,
                padding: '2px 8px',
                cursor: 'pointer',
                fontSize: 12
              }}
            >
              Back
            </Box>
          )}
          <IconButton
            size="small"
            onClick={handleToggleSearch}
            aria-label={isSearchMode ? 'Close search' : 'Search threads'}
            sx={{ color: isSearchMode ? 'primary.main' : 'text.secondary' }}
          >
            <SearchOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton size="small" onClick={handleMinimizeToggle} aria-label="Minimize history menu" sx={{ color: 'text.secondary' }}>
            <ChevronLeftIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      {/* Optional search input row */}
      {isSearchMode && (
        <Box sx={{ px: 1.5, pt: 1, pb: 0.5 }}>
          <Input
            inputRef={inputRef}
            placeholder="Search threads…"
            value={searchTerm}
            onChange={(e) => {
              setSearchPage(1);
              setSearchTerm(e.target.value);
            }}
            sx={{
              width: '100%',
              height: 34,
              '& .MuiInputBase-input': { py: 0.5 },
              '&:before': { borderBottomColor: 'divider' },
              '&:hover:not(.Mui-disabled):before': { borderBottomColor: 'text.primary' }
            }}
            startAdornment={
              <InputAdornment position="start">
                <SearchOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              </InputAdornment>
            }
            endAdornment={
              (searchTerm || '').trim().length > 0 ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Clear search"
                    size="small"
                    edge="end"
                    onClick={() => {
                      setSearchTerm('');
                      setSearchPage(1);
                      setSearchResults([]);
                      setSearchNumPages(1);
                      try {
                        inputRef.current && inputRef.current.blur && inputRef.current.blur();
                      } catch (_) {
                        /* ignore */
                      }
                    }}
                  >
                    <CloseRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  </IconButton>
                </InputAdornment>
              ) : null
            }
          />
        </Box>
      )}

      {/* List */}
      <Box sx={{ pt: isSearchMode ? 0.5 : 1, flex: 1, minHeight: 0 }}>
        <Box
          ref={listRef}
          sx={{
            height: '100%',
            overflowY: 'auto',
            overflowX: 'hidden',
            overscrollBehavior: 'contain'
          }}
          onScroll={handleScroll}
          onWheelCapture={handleWheelCapture}
        >
          <Box sx={{ px: 1, pb: 1 }}>
            {Array.isArray(effectiveItems) && effectiveItems.length === 0 ? (
              isSearchMode ? (
                (searchTerm || '').trim().length < 3 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    Type at least 3 characters to search.
                  </Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                    No results found.
                  </Typography>
                )
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
                  No threads yet.
                </Typography>
              )
            ) : (
              (Array.isArray(effectiveItems) ? effectiveItems : []).map((t) => (
                <Card
                  key={t.id}
                  elevation={0}
                  sx={{
                    mb: 0.25,
                    borderRadius: 0.75,
                    cursor: 'pointer',
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: (th) => th.palette.background.paper,
                    transition: 'background-color 0.18s ease, transform 0.02s ease, border-color 0.18s ease',
                    '&:hover': {
                      borderColor: 'primary.light',
                      backgroundColor: (th) => th.palette.action.hover
                    },
                    '&:active': { transform: 'translateY(1px)' }
                  }}
                  onClick={() => handleOpenThread(t.id)}
                  role="button"
                >
                  <CardContent sx={{ py: 0.5, px: 0.75 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle2" noWrap>
                        {t.title || t.name || 'Untitled thread'}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              ))
            )}
            {effectiveLoading && Array.isArray(effectiveItems) && effectiveItems.length > 0 ? renderSkeletons(3) : null}
          </Box>
        </Box>
      </Box>

      {/* Footer removed – infinite scroll replaces pagination */}
    </Box>
  );
};

export default AgentHistoryMenu;
